import { getFullName } from "../../careprovider/visits/utils";

const VOLUNTEER_REQUEST_CREATED = "niramaya.volunteer.request.created";
const VOLUNTEER_REQUEST_UPDATED = "niramaya.volunteer.request.updated";
const PROFILE_DETAIL_EVENTS = new Set([
  "niramaya.patient.updated",
  "niramaya.patient.data.updated",
  "niramaya.patient.details.updated",
  "niramaya.workforce.updated",
  "niramaya.workforce.data.updated",
  "niramaya.workforce.details.updated",
]);
export const LOGIN_FAILED_EVENT = "niramaya.user.login.failed";
export const LOGIN_FAILED_EVENT_TITLE = "Failed Login";

const LOGIN_FAILED_DESCRIPTIONS = {
  identity_provider_error: "Sign-in was rejected by the identity provider.",
  missing_role: "Sign-in failed because the account has no role.",
  user_not_found: "Sign-in failed because the user was not found.",
  authentication_failed: "Sign-in failed during biometric authentication.",
  login_exception: "Sign-in failed because of an unexpected error.",
  pin_attempts_exceeded:
    "Sign-in failed after too many incorrect PIN attempts.",
};

const getFailedLoginDescription = (currentValues = {}) => {
  const { reason, attemptsRemaining, errorDescription } = currentValues;

  if (reason === "incorrect_pin") {
    const remaining = Number(attemptsRemaining);
    if (Number.isFinite(remaining)) {
      const label = remaining === 1 ? "attempt" : "attempts";
      return `Incorrect PIN entered. ${remaining} ${label} remaining.`;
    }
    return "Incorrect PIN entered.";
  }

  if (
    reason === "identity_provider_error" &&
    /cancel/i.test(errorDescription || "")
  ) {
    return "The user cancelled sign-in.";
  }

  return LOGIN_FAILED_DESCRIPTIONS[reason] || "Sign-in failed.";
};
const DISPLAY_DATE_OPTIONS = { month: "short", day: "numeric", year: "numeric" };
const EMPTY_CHANGES = { oldValues: {}, newValues: {} };

const hasOwn = (object, key) =>
  Object.prototype.hasOwnProperty.call(object, key);

const formatDate = (dateStr, options = { dateStyle: "short" }) => {
  return new Date(dateStr).toLocaleDateString("en-US", options);
};

const formatTime = (timeStr) => {
  return new Date(timeStr).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getServicesString = (services) => {
  if (!services) return "";

  const parsedServices = JSON.parse(services).map((item) => JSON.parse(item));
  const titles = parsedServices.map((item) => item.title);

  return titles.length > 1
    ? `${titles.slice(0, -1).join(", ")} and ${titles[titles.length - 1]}`
    : titles[0] || "";
};

const safeGetServicesString = (services) => {
  try {
    return getServicesString(services) || "";
  } catch {
    return "";
  }
};

const formatDisplayDate = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return formatDate(value, DISPLAY_DATE_OPTIONS);
};

const formatPatientAvailability = (value) => {
  if (!value) return "";
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    if (Array.isArray(parsed)) return parsed.filter(Boolean).join(", ");
    return String(value);
  } catch {
    return String(value);
  }
};

const replacePlaceholders = (str, replacements) => {
  return Object.entries(replacements).reduce(
    (result, [key, value]) =>
      result?.replaceAll(`{${key}}`, value || ""),
    str,
  );
};

function createChangeString(values, prefix) {
  if (!Object.keys(values).length) return "";

  const valuesString = Object.entries(values)
    .map(([key, value]) => `${key}: ${value}`)
    .join(", ");
  return `${prefix} ${valuesString}`;
}

function compareObjects(newObj, oldObj) {
  if (oldObj == null || newObj == null) return EMPTY_CHANGES;

  const oldValues = {};
  const newValues = {};

  const formatKeyValue = (key, value) => {
    if (key.includes("Date")) return formatDate(value);
    if (key.includes("Time")) return formatTime(value);
    return value;
  };

  Object.keys(newObj)
    .filter(
      (key) =>
        hasOwn(oldObj, key) &&
        !key.includes("Identifier") &&
        !key.includes("comments"),
    )
    .forEach((key) => {
      const formattedOldValue = formatKeyValue(key, oldObj[key]);
      const formattedNewValue = formatKeyValue(key, newObj[key]);
      const displayKey = key.includes("Status") ? "status" : key;

      if (formattedNewValue !== formattedOldValue) {
        oldValues[displayKey] = formattedOldValue;
        newValues[displayKey] = formattedNewValue;
      }
    });

  return { oldValues, newValues };
}

const hasTextValue = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

const buildChangeSentence = (label, oldVal, newVal) => {
  const hasOld = hasTextValue(oldVal);
  const hasNew = hasTextValue(newVal);

  if (!hasOld && hasNew) {
    if (label === "Notes" || label === "Availability Notes") {
      return `${label} added: "${newVal}"`;
    }
    if (label === "Volunteer") {
      return `Volunteer assigned: ${newVal}`;
    }
    return `${label} set to "${newVal}"`;
  }

  if (hasOld && !hasNew) {
    if (label === "Notes" || label === "Availability Notes") {
      return `${label} removed`;
    }
    if (label === "Volunteer") {
      return `Volunteer removed: ${oldVal}`;
    }
    return `${label} cleared`;
  }

  if (hasOld && hasNew && String(oldVal) !== String(newVal)) {
    return `${label} changed from "${oldVal}" to "${newVal}"`;
  }

  return "";
};

const getVolunteerName = (opportunity) => {
  const workforce = opportunity?.workforce;
  if (typeof workforce?.name === "string" && workforce.name.trim()) {
    return workforce.name.trim();
  }
  return (
    getFullName(workforce?.givenName, workforce?.familyName) ||
    getFullName(opportunity?.givenName, opportunity?.familyName) ||
    ""
  );
};

const parseList = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getWorkforceId = (opportunity) =>
  opportunity?.workforceIdentifier ||
  opportunity?.workforce?.userIdentifier ||
  opportunity?.userIdentifier;

const getIdsByOpportunityStatus = (opportunities, status) => {
  const ids = parseList(opportunities)
    .filter((opportunity) => opportunity?.opportunityStatus === status)
    .map(getWorkforceId)
    .filter(Boolean);
  return [...new Set(ids)];
};

const namesFromFrequency = (frequency) => {
  const nameById = {};
  parseList(frequency).forEach((entry) => {
    parseList(entry?.slots).forEach((slot) => {
      const volunteer = slot?.assignedVolunteer;
      const id = volunteer?.userIdentifier;
      const name =
        (typeof volunteer?.name === "string" && volunteer.name.trim()) ||
        getFullName(volunteer?.givenName, volunteer?.familyName);
      if (id && name) nameById[id] = name;
    });
  });
  return nameById;
};

const buildVolunteerNameMap = (opportunitiesList) => {
  const nameById = {};
  parseList(opportunitiesList).forEach((opportunity) => {
    const name = getVolunteerName(opportunity);
    const id = getWorkforceId(opportunity);
    if (id && name) nameById[id] = name;
  });
  return nameById;
};

const joinNames = (names) => {
  const unique = [...new Set(names.filter(Boolean))];
  if (!unique.length) return "";
  if (unique.length === 1) return unique[0];
  return `${unique.slice(0, -1).join(", ")} and ${unique[unique.length - 1]}`;
};

const volunteerCountLabel = (count) =>
  `${count} ${count === 1 ? "volunteer" : "volunteers"}`;

const describeOpportunityGroup = (status, ids, nameById, { removed = false } = {}) => {
  const names = joinNames(ids.map((id) => nameById[id]));
  const countLabel = volunteerCountLabel(ids.length);

  if (status === "Assigned") {
    if (removed) {
      return names
        ? `Direct assignment removed for ${names}`
        : `Direct assignment removed for ${countLabel}`;
    }
    return names
      ? `Directly assigned to ${names}`
      : `Directly assigned to ${countLabel}`;
  }

  if (removed) {
    return names ? `No longer shared with ${names}` : `No longer shared with ${countLabel}`;
  }
  return names ? `Shared with ${names}` : `Shared with ${countLabel}`;
};

const describeStatusSwitch = (ids, nameById, sentence) => {
  if (!ids.length) return "";
  const names = joinNames(ids.map((id) => nameById[id]));
  return names ? `${names} ${sentence}` : `${volunteerCountLabel(ids.length)} ${sentence}`;
};

const describeSameStatusReplacement = (status, removedIds, addedIds, nameById) => {
  if (removedIds.length !== 1 || addedIds.length !== 1) return "";
  const fromName = nameById[removedIds[0]];
  const toName = nameById[addedIds[0]];
  if (!fromName || !toName) return "";
  return status === "Assigned"
    ? `Direct assignment changed from ${fromName} to ${toName}`
    : `Sharing changed from ${fromName} to ${toName}`;
};

const appendOpportunityStatusChange = (
  sentences,
  status,
  previousOpportunities,
  currentOpportunities,
  nameById,
  skipIds,
) => {
  const oldIds = getIdsByOpportunityStatus(previousOpportunities, status);
  const newIds = getIdsByOpportunityStatus(currentOpportunities, status);
  const removedIds = oldIds.filter((id) => !newIds.includes(id) && !skipIds.has(id));
  const addedIds = newIds.filter((id) => !oldIds.includes(id) && !skipIds.has(id));

  if (!removedIds.length && !addedIds.length) return;

  const replacement = describeSameStatusReplacement(
    status,
    removedIds,
    addedIds,
    nameById,
  );
  if (replacement) {
    sentences.push(replacement);
    return;
  }

  if (addedIds.length) {
    sentences.push(describeOpportunityGroup(status, addedIds, nameById));
  }
  if (removedIds.length) {
    sentences.push(
      describeOpportunityGroup(status, removedIds, nameById, { removed: true }),
    );
  }
};

const getAssignmentSummary = (opportunities, nameById) => {
  const parts = [];
  const assignedIds = getIdsByOpportunityStatus(opportunities, "Assigned");
  const sharedIds = getIdsByOpportunityStatus(opportunities, "Shared");

  if (assignedIds.length) {
    parts.push(describeOpportunityGroup("Assigned", assignedIds, nameById));
  }
  if (sharedIds.length) {
    parts.push(describeOpportunityGroup("Shared", sharedIds, nameById));
  }

  return parts.join(". ");
};

const getVolunteerNameMap = (opportunities, extraNames, ...frequencies) => ({
  ...(extraNames || {}),
  ...frequencies.reduce(
    (names, frequency) => ({ ...names, ...namesFromFrequency(frequency) }),
    {},
  ),
  ...buildVolunteerNameMap(parseList(opportunities)),
});

const getVolunteerRequestCreatedSummary = (currentValues, extraNames) => {
  if (!currentValues || typeof currentValues !== "object") return "";

  const parts = [];
  if (hasTextValue(currentValues.status)) {
    parts.push(`Status: ${currentValues.status}`);
  }

  const opportunities = parseList(currentValues.opportunities);
  const assignment = getAssignmentSummary(
    opportunities,
    getVolunteerNameMap(opportunities, extraNames, currentValues.frequency),
  );
  if (assignment) parts.push(assignment);
  if (!parts.length) return "";

  return `${parts.join(". ")}.`;
};

const getVolunteerChangeSentence = (previousValues, currentValues, extraNames) => {
  const oldOpportunities = parseList(previousValues?.opportunities);
  const newOpportunities = parseList(currentValues?.opportunities);
  const nameById = getVolunteerNameMap(
    [...oldOpportunities, ...newOpportunities],
    extraNames,
    previousValues?.frequency,
    currentValues?.frequency,
  );

  const oldAssigned = getIdsByOpportunityStatus(oldOpportunities, "Assigned");
  const newAssigned = getIdsByOpportunityStatus(newOpportunities, "Assigned");
  const oldShared = getIdsByOpportunityStatus(oldOpportunities, "Shared");
  const newShared = getIdsByOpportunityStatus(newOpportunities, "Shared");

  const switchedToAssigned = oldShared.filter(
    (id) => newAssigned.includes(id) && !oldAssigned.includes(id),
  );
  const switchedToShared = oldAssigned.filter(
    (id) => newShared.includes(id) && !oldShared.includes(id),
  );

  const sentences = [
    describeStatusSwitch(
      switchedToAssigned,
      nameById,
      "changed from shared to directly assigned",
    ),
    describeStatusSwitch(
      switchedToShared,
      nameById,
      "changed from directly assigned to shared",
    ),
  ].filter(Boolean);

  const skipIds = new Set([...switchedToAssigned, ...switchedToShared]);
  appendOpportunityStatusChange(
    sentences,
    "Assigned",
    oldOpportunities,
    newOpportunities,
    nameById,
    skipIds,
  );
  appendOpportunityStatusChange(
    sentences,
    "Shared",
    oldOpportunities,
    newOpportunities,
    nameById,
    skipIds,
  );

  return sentences.join("; ");
};

const appendFieldChange = (changes, label, oldVal, newVal) => {
  const sentence = buildChangeSentence(label, oldVal, newVal);
  if (sentence) changes.push(sentence);
};

const VOLUNTEER_REQUEST_FIELDS = [
  { key: "status", label: "Status" },
  { key: "startDate", label: "Date", format: formatDisplayDate },
  { key: "requestDate", label: "Request Date", format: formatDisplayDate },
  { key: "services", label: "Service", format: safeGetServicesString },
  { key: "requestSource", label: "Request Source" },
  { key: "additionalComments", label: "Notes" },
  {
    key: "patientAvailabilityComments",
    label: "Availability Notes",
  },
  {
    key: "patientAvailability",
    label: "Patient Availability",
    format: formatPatientAvailability,
  },
  { key: "frequencyPeriod", label: "Frequency" },
];

const appendConfiguredFieldChanges = (
  changes,
  currentValues,
  previousValues,
) => {
  VOLUNTEER_REQUEST_FIELDS.forEach(({ key, label, format = String }) => {
    if (!hasOwn(previousValues, key) || !hasOwn(currentValues, key)) return;

    const oldValue =
      previousValues[key] == null ? "" : format(previousValues[key]);
    const newValue =
      currentValues[key] == null ? "" : format(currentValues[key]);

    if (oldValue !== newValue) {
      appendFieldChange(changes, label, oldValue, newValue);
    }
  });
};

/**
 * Build a human-readable summary of volunteer-request field changes from
 * event payload currentValues / previousValues. Returns "" when details
 * are unavailable so the generic description is preserved.
 */
const getVolunteerRequestChangeSummary = (
  currentValues,
  previousValues,
  extraNames,
) => {
  if (
    !currentValues ||
    !previousValues ||
    typeof previousValues !== "object" ||
    !Object.keys(previousValues).length
  ) {
    return "";
  }

  const changes = [];
  appendConfiguredFieldChanges(changes, currentValues, previousValues);

  if (
    hasOwn(previousValues, "opportunities") ||
    hasOwn(currentValues, "opportunities")
  ) {
    const volunteerChange = getVolunteerChangeSentence(
      previousValues,
      currentValues,
      extraNames,
    );
    if (volunteerChange) changes.push(volunteerChange);
  }

  if (!changes.length) return "";
  return `${changes.join("; ")}.`;
};

const getEventDate = (data, currentValues) => {
  const eventType = data?.type || data?.eventType || "";

  if (eventType.includes("opportunity")) {
    return formatDate(currentValues?.opportunityDate);
  }
  if (eventType.includes("delivery")) {
    return formatDate(currentValues?.deliveryDate);
  }
  return undefined;
};

const getDescriptionReplacements = (data, currentValues, previousValues) => {
  const fullName = getFullName(
    currentValues?.givenName,
    currentValues?.familyName,
  );
  const memberships = currentValues?.memberships;
  const { oldValues, newValues } = compareObjects(
    currentValues,
    previousValues,
  );

  return {
    date: getEventDate(data, currentValues),
    startTime: formatTime(currentValues?.startTime),
    endTime: formatTime(currentValues?.endTime),
    patientName: fullName,
    forPatientName: fullName && `for ${fullName}`,
    workforceName: fullName,
    emailSubject: `${currentValues?.templates?.email?.subject}${currentValues?.patient ? ` for ${currentValues.patient}` : ""}${currentValues?.workforce ? ` sent to ${currentValues.workforce}` : ""}`,
    careCircleName: currentValues?.groupName,
    formName: currentValues?.formName,
    username: data?.userName,
    services: safeGetServicesString(currentValues?.services),
    groupName: currentValues?.group?.groupName,
    serviceName: currentValues?.title || currentValues?.serviceTitle,
    newServiceName: currentValues?.title,
    oldServiceName: previousValues?.title,
    careCircle: memberships?.[0]?.group?.groupName || "",
    memberName: memberships
      ? getFullName(
          memberships?.[0]?.givenName,
          memberships?.[0]?.givenName,
        )
      : "",
    cardBrand: currentValues?.cardBrand,
    cardLastFourDigits: currentValues?.cardLastFourDigits,
    oldValues: createChangeString(oldValues, "from"),
    newValues: createChangeString(newValues, "to"),
  };
};

const appendSummary = (baseText, summary) => {
  if (!summary) return baseText;
  const base = (baseText || "").trim().replace(/\.$/, "");
  return base ? `${base}. ${summary}` : summary;
};

const PROFILE_FIELD_LABELS = {
  givenName: "First Name",
  familyName: "Last Name",
  preferredName: "Preferred Name",
  pronoun: "Pronoun",
  email: "Email",
  telephone: "Phone",
  gender: "Gender",
  birthDate: "Date of Birth",
  comments: "Notes",
  jobTitle: "Role",
  employeeCode: "Employee Code",
  anniversary: "Volunteer Anniversary",
  workforceStatus: "Status",
  patientStatus: "Status",
  isActive: "Active",
  isPPC: "PPC",
  isFPC: "FPC",
  medicalRecordNumber: "Medical Record No.",
  admitDate: "Admit Date",
  certificateEndDate: "Certificate End Date",
  serviceLocationType: "Service Location",
  levelOfCare: "Level of Care",
  teamName: "Team",
  teams: "Team",
  facilityName: "Facility",
  room: "Room",
  apartmentNumber: "Apartment",
  streetAddress: "Street Address",
  addressLocality: "City",
  addressRegion: "State",
  postalCode: "Postal Code",
  completeAddress: "Address",
  addressCountry: "Country",
  diagnosis: "Primary Diagnosis",
  referral: "Referral",
};

const PROFILE_SKIP_KEYS = new Set([
  "identifier",
  "id",
  "metadata",
  "address",
  "data",
  "password",
  "pin",
  "secretkey",
  "token",
  "icon",
  "profilepicture",
  "latitude",
  "longitude",
  "facilityid",
]);

const BOOLEAN_PROFILE_KEYS = new Set(["isActive", "isPPC", "isFPC"]);
const DATE_PROFILE_KEYS = new Set([
  "birthDate",
  "admitDate",
  "certificateEndDate",
  "anniversary",
]);

const humanizeKey = (key) => {
  if (PROFILE_FIELD_LABELS[key]) return PROFILE_FIELD_LABELS[key];
  const spaced = String(key)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const shouldSkipProfileKey = (key) => {
  const normalized = String(key || "").toLowerCase();
  return (
    !normalized ||
    PROFILE_SKIP_KEYS.has(normalized) ||
    normalized.includes("identifier") ||
    normalized.includes("timestamp") ||
    normalized.includes("password")
  );
};

const parseRecordObject = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim().startsWith("{")) return null;
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed;
    }
  } catch {
    return null;
  }
  return null;
};

const unwrapProfileRecord = (value) => {
  const nested = value?.data;
  if (
    nested &&
    typeof nested === "object" &&
    !Array.isArray(nested) &&
    (nested.givenName || nested.familyName || nested.email || nested.medicalRecordNumber)
  ) {
    return nested;
  }
  return value;
};

const formatListValue = (value) => {
  const list = Array.isArray(value) ? value : parseList(value);
  return list
    .map((item) => {
      if (typeof item === "string" || typeof item === "number") return String(item);
      if (item?.facilityName) return item.facilityName;
      if (item?.name) return item.name;
      if (item?.title) return item.title;
      if (item?.teamName) return item.teamName;
      return getFullName(item?.givenName, item?.familyName) || "";
    })
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
    .join(", ");
};

const formatProfileValue = (key, value) => {
  if (value === null || value === undefined || value === "") return "";
  if (BOOLEAN_PROFILE_KEYS.has(key) || typeof value === "boolean") {
    if (value === true || value === "true") return "Yes";
    if (value === false || value === "false") return "No";
  }
  if (DATE_PROFILE_KEYS.has(key) || /date/i.test(key)) {
    return formatDisplayDate(value);
  }
  if (key === "serviceLocationType") {
    if (value === "home") return "Home";
    if (value === "facility") return "Facility";
  }
  if (Array.isArray(value) || (typeof value === "string" && value.trim().startsWith("["))) {
    return formatListValue(value);
  }
  if (typeof value === "object") return "";
  return String(value).trim();
};

const setProfileField = (fields, label, value) => {
  if (!label) return;
  const formatted = value ?? "";
  if (fields[label] && !formatted) return;
  fields[label] = formatted;
};

const flattenPerson = (fields, person, prefix) => {
  const record = parseRecordObject(person);
  if (!record) return;
  const labels = {
    givenName: `${prefix} First Name`,
    familyName: `${prefix} Last Name`,
    telephone: `${prefix} Phone`,
    caregiverRelation: `${prefix} Relation`,
    relation: `${prefix} Relation`,
    completeAddress: `${prefix} Address`,
    email: `${prefix} Email`,
  };

  Object.entries(record).forEach(([key, value]) => {
    if (shouldSkipProfileKey(key)) return;
    setProfileField(
      fields,
      labels[key] || `${prefix} ${humanizeKey(key)}`,
      formatProfileValue(key, value),
    );
  });
};

const flattenMetadata = (fields, metadata) => {
  const record = parseRecordObject(metadata);
  if (!record) return;

  Object.entries(record).forEach(([key, value]) => {
    if (key === "careGiver") {
      flattenPerson(fields, value, "Caregiver");
      return;
    }
    if (key === "emergencyContact") {
      flattenPerson(fields, value, "Emergency Contact");
      return;
    }
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const selected = Object.entries(value)
        .filter(([, checked]) => checked === true || checked === "true")
        .map(([name]) => humanizeKey(name))
        .sort((a, b) => a.localeCompare(b));
      setProfileField(fields, humanizeKey(key), selected.join(", "));
      return;
    }
    const label = key === "telephone" ? "Work Phone" : humanizeKey(key);
    setProfileField(fields, label, formatProfileValue(key, value));
  });
};

const collectProfileFields = (record) => {
  const fields = {};
  const source = unwrapProfileRecord(record);
  if (!source || typeof source !== "object") return fields;

  Object.entries(source).forEach(([key, value]) => {
    if (shouldSkipProfileKey(key)) return;

    if (key === "address") {
      const address = parseRecordObject(value) || {};
      Object.entries(address).forEach(([addressKey, addressValue]) => {
        if (shouldSkipProfileKey(addressKey)) return;
        setProfileField(
          fields,
          humanizeKey(addressKey),
          formatProfileValue(addressKey, addressValue),
        );
      });
      return;
    }

    if (key === "metadata") {
      flattenMetadata(fields, value);
      return;
    }

    if (key === "facility" && value && typeof value === "object") {
      setProfileField(fields, "Facility", value.facilityName || value.name || "");
      return;
    }

    if (key === "preferences" || key === "preference") {
      const preferences = parseRecordObject(value);
      if (preferences) {
        Object.entries(preferences).forEach(([preferenceKey, preferenceValue]) => {
          setProfileField(
            fields,
            humanizeKey(preferenceKey),
            formatProfileValue(preferenceKey, preferenceValue),
          );
        });
        return;
      }
    }

    if (key === "diagnosis" || key === "teams") {
      setProfileField(fields, humanizeKey(key), formatListValue(value));
      return;
    }

    if (value && typeof value === "object") return;
    setProfileField(fields, humanizeKey(key), formatProfileValue(key, value));
  });

  return fields;
};

const getProfilePictureChange = (currentValues, previousValues) => {
  const current = unwrapProfileRecord(currentValues) || {};
  const previous = unwrapProfileRecord(previousValues) || {};
  if (!hasOwn(current, "profilePicture") && !hasOwn(previous, "profilePicture")) {
    return "";
  }
  const oldPicture = previous.profilePicture;
  const newPicture = current.profilePicture;
  if (String(oldPicture || "") === String(newPicture || "")) return "";
  if (!hasTextValue(newPicture)) return "Profile picture removed";
  if (!hasTextValue(oldPicture)) return "Profile picture added";
  return "Profile picture updated";
};

const getProfileChangeSummary = (currentValues, previousValues) => {
  if (
    !currentValues ||
    !previousValues ||
    typeof previousValues !== "object" ||
    !Object.keys(previousValues).length
  ) {
    return "";
  }

  const currentFields = collectProfileFields(currentValues);
  const previousFields = collectProfileFields(previousValues);
  const changes = [];
  const pictureChange = getProfilePictureChange(currentValues, previousValues);
  if (pictureChange) changes.push(pictureChange);

  new Set([
    ...Object.keys(previousFields),
    ...Object.keys(currentFields),
  ]).forEach((label) => {
    if (!hasOwn(currentFields, label)) return;
    appendFieldChange(
      changes,
      label,
      hasOwn(previousFields, label) ? previousFields[label] : "",
      currentFields[label],
    );
  });

  if (!changes.length) return "";
  return `${changes.join("; ")}.`;
};

/**
 * Workforce identifiers on volunteer request events whose names are not
 * stored in the event payload, so they can be resolved in one lookup.
 */
export const getUnnamedVolunteerIds = (records = []) => {
  const ids = new Set();

  records.forEach((record) => {
    const eventType = record?.type || record?.eventType;
    if (
      eventType !== VOLUNTEER_REQUEST_CREATED &&
      eventType !== VOLUNTEER_REQUEST_UPDATED
    ) {
      return;
    }

    const { currentValues, previousValues } = record?.payload || {};
    const opportunities = [
      ...parseList(currentValues?.opportunities),
      ...parseList(previousValues?.opportunities),
    ];
    const nameById = getVolunteerNameMap(
      opportunities,
      null,
      currentValues?.frequency,
      previousValues?.frequency,
    );

    opportunities.forEach((opportunity) => {
      const id = getWorkforceId(opportunity);
      if (id && !nameById[id]) ids.add(id);
    });
  });

  return [...ids];
};

export const buildWorkforceNameMap = (workforceById = {}) =>
  Object.entries(workforceById || {}).reduce((names, [id, workforce]) => {
    const name =
      (typeof workforce?.name === "string" && workforce.name.trim()) ||
      getFullName(workforce?.givenName, workforce?.familyName);
    if (name) names[id] = name;
    return names;
  }, {});

export const descriptionString = (string, data, workforceNames) => {
  const { currentValues, previousValues } = data?.payload || {};
  const eventType = data?.type || data?.eventType;

  if (eventType === LOGIN_FAILED_EVENT) {
    return getFailedLoginDescription(currentValues);
  }

  const replacements = getDescriptionReplacements(
    data,
    currentValues,
    previousValues,
  );
  let result = replacePlaceholders(string, replacements);

  if (eventType === VOLUNTEER_REQUEST_CREATED) {
    result = appendSummary(
      result,
      getVolunteerRequestCreatedSummary(currentValues, workforceNames),
    );
  }

  if (eventType === VOLUNTEER_REQUEST_UPDATED) {
    result = appendSummary(
      result,
      getVolunteerRequestChangeSummary(
        currentValues,
        previousValues,
        workforceNames,
      ),
    );
  }

  if (PROFILE_DETAIL_EVENTS.has(eventType)) {
    result = appendSummary(
      result,
      getProfileChangeSummary(currentValues, previousValues),
    );
  }

  return result;
};
